import { Server } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BASE_URL } from '@/lib/basePath';

// Import server type from shared
import type { ServerType } from '@tracearr/shared';

interface MediaServerIconProps {
  /** Server type: 'plex', 'jellyfin', 'emby', or 'dispatcharr' */
  type: ServerType;
  /** CSS class name for sizing (e.g., "h-4 w-4") */
  className?: string;
  /** Alt text for accessibility */
  alt?: string;
}

/**
 * Displays the appropriate icon for a media server type.
 * Falls back to generic Server icon if type is unknown.
 */
export function MediaServerIcon({ type, className, alt }: MediaServerIconProps) {
  const iconPath = getIconPath(type);

  if (!iconPath) {
    // Fallback to generic server icon
    return <Server className={className} />;
  }

  return (
    <img src={iconPath} alt={alt ?? `${type} server`} className={cn('object-contain', className)} />
  );
}

function getIconPath(type: ServerType): string | null {
  const base = BASE_URL;
  const normalizedType = String(type).trim().toLowerCase();
  switch (normalizedType) {
    case 'plex':
      return `${base}images/servers/plex.png`;
    case 'jellyfin':
      return `${base}images/servers/jellyfin.png`;
    case 'emby':
      return `${base}images/servers/emby.png`;
    case 'dispatcharr':
      return `${base}images/servers/dispatcharr.png`;
    default:
      if (normalizedType.includes('dispatch')) {
        return `${base}images/servers/dispatcharr.png`;
      }
      return null;
  }
}
